import axios from 'axios';

import {
  APP_DEFAULT_LANGUAGE,
  APP_LANGUAGE_COOKIE_NAME,
  APP_VERSION,
  REFRESH_TOKEN_SS_KEY,
  USER_EXPIRATION_TIME_SS_KEY,
  USER_TOKEN_SS_KEY,
} from '../../configs/app.config';
import {
  APP_PARTNER_ID_HEADER,
  APP_VERSION_HEADER,
  AUTH_HEADER,
  AUTH_TOKEN_PREFIX,
  LANGUAGE_HEADER,
} from '../../configs/services/services.config';
import { checkUserTokenExpire } from '../../utils/user/user.util';
import { isClient } from '../../utils/next/next.util';
import { getPartnerId } from '../../utils/partner/partner.util';

import { postRefreshToken } from './auth/auth.service';

import type { InternalAxiosRequestConfig } from 'axios';

export const apiService = axios.create();

const addHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (isClient()) {
    const newConfig = config;
    const localToken = sessionStorage.getItem(USER_TOKEN_SS_KEY) ?? '';
    const chosenPartnerId = getPartnerId();

    newConfig.headers[LANGUAGE_HEADER] =
      localStorage.getItem(APP_LANGUAGE_COOKIE_NAME) ?? APP_DEFAULT_LANGUAGE;

    if (localToken !== '') {
      newConfig.headers[AUTH_HEADER] = `${AUTH_TOKEN_PREFIX} ${localToken}`;
    }

    newConfig.headers[APP_PARTNER_ID_HEADER] = chosenPartnerId;
    newConfig.headers[APP_VERSION_HEADER] = APP_VERSION;

    return newConfig;
  }

  return config;
};

// We need refresh token only one for few request in same time
let refreshTokenPromise: Promise<string> | null = null;
let requestQueue: Array<(config: InternalAxiosRequestConfig) => void> = [];

const refreshUserToken = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  if (isClient() && checkUserTokenExpire()) {
    if (refreshTokenPromise) {
      return new Promise((resolve) => {
        requestQueue.push(() => {
          resolve(config);
        });
      });
    }

    refreshTokenPromise = (async () => {
      try {
        const { token, refreshToken, expiration } = await postRefreshToken({
          token: sessionStorage.getItem(USER_TOKEN_SS_KEY) ?? '',
          refreshToken: sessionStorage.getItem(REFRESH_TOKEN_SS_KEY) ?? '',
        });

        sessionStorage.setItem(USER_TOKEN_SS_KEY, token);
        sessionStorage.setItem(REFRESH_TOKEN_SS_KEY, refreshToken);
        sessionStorage.setItem(USER_EXPIRATION_TIME_SS_KEY, expiration.toString());

        requestQueue.forEach((callback) => {
          const newConfig = { ...config };

          if (newConfig.headers[AUTH_HEADER] && token) {
            newConfig.headers[AUTH_HEADER] = `${AUTH_TOKEN_PREFIX} ${token}`;
          }

          callback(newConfig);
        });
        requestQueue = [];

        return token;
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error, 'axiosInterceptor.util:refreshUserToken');

        const controller = new AbortController();

        controller.abort();
        sessionStorage.clear();

        window.location.replace('/');

        requestQueue = [];
        throw error;
      } finally {
        refreshTokenPromise = null;
      }
    })();

    const newToken = await refreshTokenPromise;
    const newConfig = config;

    if (newConfig.headers[AUTH_HEADER] && newToken) {
      newConfig.headers[AUTH_HEADER] = `${AUTH_TOKEN_PREFIX} ${newToken}`;
    }

    return newConfig;
  }

  return config;
};

apiService.interceptors.request.use(addHeaders);
apiService.interceptors.request.use(refreshUserToken);
